(() => {
  const encoder = new TextEncoder();
  const bytes = value => typeof value === 'string' ? encoder.encode(value) : value;
  function join(parts) {
    const arrays = parts.map(bytes);
    const result = new Uint8Array(arrays.reduce((sum, value) => sum + value.length, 0));
    let position = 0;
    for (const array of arrays) { result.set(array, position); position += array.length; }
    return result;
  }
  function makePdf(images) {
    const pt = mm => (mm * 72 / 25.4).toFixed(4);
    const objects = ['<< /Type /Catalog /Pages 2 0 R >>', '<< /Type /Pages /Kids [3 0 R 6 0 R] /Count 2 >>'];
    for (let index = 0; index < 2; index++) {
      const base = 3 + index * 3;
      objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pt(99)} ${pt(69)}] /TrimBox [${pt(7)} ${pt(7)} ${pt(92)} ${pt(62)}] /BleedBox [${pt(4)} ${pt(4)} ${pt(95)} ${pt(65)}] /Resources << /XObject << /Im0 ${base + 2} 0 R >> >> /Contents ${base + 1} 0 R >>`);
      let commands = `q ${pt(91)} 0 0 ${pt(61)} ${pt(4)} ${pt(4)} cm /Im0 Do Q\n0 G 0.25 w\n`;
      for (const y of [7, 62]) for (const x of [0.5, 95.5]) commands += `${pt(x)} ${pt(y)} m ${pt(x + 3)} ${pt(y)} l S\n`;
      for (const x of [7, 92]) for (const y of [0.5, 65.5]) commands += `${pt(x)} ${pt(y)} m ${pt(x)} ${pt(y + 3)} l S\n`;
      objects.push(`<< /Length ${bytes(commands).length} >>\nstream\n${commands}endstream`);
      const image = images[index];
      objects.push(join([`<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.data.length} >>\nstream\n`, image.data, '\nendstream']));
    }
    const parts = ['%PDF-1.4\n% EPT Rotary personal cards\n'];
    const offsets = [0];
    let length = bytes(parts[0]).length;
    for (let i = 0; i < objects.length; i++) {
      offsets.push(length);
      const part = join([`${i + 1} 0 obj\n`, objects[i], '\nendobj\n']);
      parts.push(part); length += part.length;
    }
    const xref = length;
    parts.push(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n` + offsets.slice(1).map(offset => String(offset).padStart(10, '0') + ' 00000 n \n').join(''));
    parts.push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
    return new Blob([join(parts)], {type: 'application/pdf'});
  }
  class EptCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-card-form]');
      this.texts = JSON.parse(this.querySelector('[data-card-texts]').textContent);
      this.approval = this.querySelector('[data-card-approved]');
      this.pack = this.querySelector('[data-card-pack]');
      this.error = this.querySelector('[data-card-error]');
      this.submit = this.querySelector('[data-card-submit]');
      this.download = this.querySelector('[data-card-download]');
      this.revision = 0;
      this.logo = new Image();
      this.logo.crossOrigin = 'anonymous';
      this.logoReady = new Promise((resolve, reject) => { this.logo.onload = resolve; this.logo.onerror = reject; });
      this.logoReady.catch(() => {this.error.textContent = this.texts.error;});
      this.logo.src = this.dataset.logo;
      this.proofLogo = window.EptProof.logo(this.dataset.watermark).then(logo => {this.watermarkLogo = logo; return logo;});
      this.proofLogo.then(() => this.render()).catch(() => {this.download.disabled = true; this.error.textContent = this.texts.error;});
      this.form.addEventListener('input', event => {
        if (event.target === this.approval) return;
        this.revision++; this.approval.checked = false; this.render();
      });
      this.form.addEventListener('change', event => {
        if (event.target === this.approval) return;
        this.revision++; this.approval.checked = false; this.render();
      });
      this.form.addEventListener('submit', event => this.order(event));
      this.download.addEventListener('click', () => this.save());
      this.logoReady.then(() => this.render()).catch(() => {});
      this.render();
    }
    values() {
      return Object.fromEntries([...this.querySelectorAll('[data-card-field]')].map(field => [field.dataset.cardField, field.value.trim().replace(/\s+/g, ' ')]));
    }
    draw(canvas, side, data, bleed = 0) {
      const pixelsPerMm = 600 / 25.4;
      canvas.width = Math.round((85 + 2 * bleed) * pixelsPerMm);
      canvas.height = Math.round((55 + 2 * bleed) * pixelsPerMm);
      const context = canvas.getContext('2d');
      context.scale(pixelsPerMm, pixelsPerMm);
      context.fillStyle = '#ffffff'; context.fillRect(0, 0, 85 + 2 * bleed, 55 + 2 * bleed);
      context.translate(bleed, bleed);
      context.fillStyle = '#18222d'; context.textBaseline = 'alphabetic';
      const errors = [];
      const wrapped = (text, width, font, max) => {
        context.font = font;
        const lines = []; let line = '';
        for (const word of text.split(' ').filter(Boolean)) {
          if (context.measureText(word).width > width) {
            if (line) {lines.push(line); line = '';}
            for (const character of word) {
              if (context.measureText(line + character).width > width) {lines.push(line); line = '';}
              line += character;
            }
          } else if (line && context.measureText(line + ' ' + word).width > width) {lines.push(line); line = word;}
          else line += (line ? ' ' : '') + word;
        }
        if (line) lines.push(line);
        if (lines.length > max) errors.push(text);
        return lines.slice(0, max);
      };
      const text = (value, x, y, width, size, max = 1, align = 'left', bold = false) => {
        context.fillStyle = '#18222d'; context.textAlign = align;
        const font = `${bold ? '700' : '400'} ${size}px Arial, sans-serif`;
        const lines = wrapped(value, width, font, max);
        lines.forEach((line, index) => context.fillText(line, x, y + index * size * 1.3));
        return lines.length;
      };
      const club = data.club || this.texts.example_club;
      const logoLoaded = this.logo.complete && this.logo.naturalWidth > 0;
      if (side === 'back') {
        if (logoLoaded) context.drawImage(this.logo, 12.5, 13, 60, 60 * this.logo.naturalHeight / this.logo.naturalWidth);
        text(club, 42.5, 43, 65, 3.2, 2, 'center');
      } else {
        if (logoLoaded) context.drawImage(this.logo, 52, 6, 27, 27 * this.logo.naturalHeight / this.logo.naturalWidth);
        text(club, 65.5, 19, 29, 2.5, 3, 'center');
        const nameLines = text(data.name || this.texts.example_name, 6, 20, 43, 3.5, 2, 'left', true);
        text([data.role, data.year].filter(Boolean).join(', '), 6, 21 + nameLines * 4.6, 43, 2.6, 2);
        let contactY = 37;
        for (const [key, label] of [['telephone', this.texts.telephone], ['mobile', this.texts.mobile], ['email', '']]) {
          if (!data[key]) continue;
          const count = text((label ? label + ': ' : '') + data[key], 6, contactY, 43, 2.5, 2);
          contactY += count * 3.4;
        }
        if (contactY > 52) errors.push(this.texts.contact);
        let addressY = 36;
        const address = [data.address, [data.postcode, data.city].filter(Boolean).join(' '), data.country, data.website].filter(Boolean);
        for (const line of address) addressY += text(line, 79, addressY, 27, 2.45, 2, 'right') * 3.4;
        if (addressY > 52) errors.push(this.texts.contact);
      }
      return errors;
    }
    render() {
      const data = this.values();
      this.layoutErrors = [...this.draw(this.querySelector('[data-card-front]'), 'front', data), ...this.draw(this.querySelector('[data-card-back]'), 'back', data)];
      const price = Number(this.pack.selectedOptions[0]?.dataset.price || 0) / 100;
      this.querySelector('[data-card-price]').textContent = new Intl.NumberFormat('nb-NO', {style:'currency', currency:'NOK', minimumFractionDigits:2, maximumFractionDigits:2}).format(price);
      this.querySelector('[data-card-summary]').textContent = [data.name, [data.role, data.year].filter(Boolean).join(', '), data.club, data.email, data.mobile].filter(Boolean).join('\n');
      this.error.textContent = this.layoutErrors.length ? this.texts.overflow : '';
    }
    valid(requireApproval = false) {
      this.render();
      for (const field of this.querySelectorAll('[data-card-field]')) if (!field.reportValidity()) return false;
      if (this.layoutErrors.length) return false;
      if (requireApproval && !this.approval.reportValidity()) return false;
      return true;
    }
    async pdf(data, proof = false) {
      await this.logoReady;
      if (proof) await this.proofLogo;
      const images = [];
      for (const side of ['front', 'back']) {
        const canvas = document.createElement('canvas');
        if (this.draw(canvas, side, data, 3).length) throw new Error(this.texts.overflow);
        if (proof) window.EptProof.mark(canvas, this.watermarkLogo);
        const encoded = canvas.toDataURL('image/jpeg', 0.98).split(',')[1];
        const binary = atob(encoded);
        images.push({width:canvas.width, height:canvas.height, data:Uint8Array.from(binary, character => character.charCodeAt(0))});
      }
      return makePdf(images);
    }
    filename(data) { return 'rotary-visittkort-' + (data.name.normalize('NFKD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'korrektur') + '.pdf'; }
    async save() {
      if (!this.valid()) return;
      this.download.disabled = true;
      try {
        const data = this.values(); const pdf = await this.pdf(data, true);
        const url = URL.createObjectURL(pdf); const link = document.createElement('a');
        link.href = url; link.download = 'korrektur-' + this.filename(data); link.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch { this.error.textContent = this.texts.error; }
      finally {this.download.disabled = false;}
    }
    async order(event) {
      event.preventDefault();
      if (this.dataset.enabled !== 'true' || !this.valid(true)) return;
      const revision = this.revision; const data = this.values();
      this.submit.disabled = true; this.submit.textContent = this.texts.wait;
      try {
        const pdf = await this.pdf(data);
        if (revision !== this.revision || !this.approval.checked) throw new Error();
        const body = new FormData(); body.append('id', this.pack.value); body.append('quantity', '1');
        const labels = {name:'Navn', role:'Rotary-verv', year:'Periode', club:'Klubb eller distrikt', mobile:'Mobil', telephone:'Telefon', email:'E-post', address:'Adresse', postcode:'Postnummer', city:'Poststed', country:'Land', website:'Nettside'};
        for (const [key, label] of Object.entries(labels)) if (data[key]) body.append(`properties[${label}]`, data[key]);
        body.append('properties[Format]', '85 × 55 mm, to sider');
        body.append('properties[Korrektur]', 'Begge sider godkjent av kunden');
        body.append('properties[_ept_card_version]', 'rotary-85x55-v1');
        body.append('properties[_Trykkfil]', new File([pdf], this.filename(data), {type:'application/pdf'}));
        const root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
        const response = await fetch(root + 'cart/add.js', {method:'POST', headers:{Accept:'application/json'}, body});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error();
        location.assign(this.dataset.cart);
      } catch {
        this.error.textContent = this.texts.error; this.submit.disabled = false; this.submit.textContent = this.texts.add;
      }
    }
  }
  if (!customElements.get('ept-card')) customElements.define('ept-card', EptCard);
})();
